import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { injectIntl } from 'react-intl';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import { SimpleLoadingComponent } from '../../utils/apollo';
import PageTitle from '../../components/PageTitle';
import PremiumMenu from './PremiumMenu/index';
import uploadLogoMutation from './mutations/uploadLogoMutation';
import uploadPhotoMutation from './mutations/uploadPhotoMutation';
import updateHubsCollegeLogoMutation from './mutations/updateHubsCollegeLogoMutation';
import updateHubsWebTourMutation from './mutations/updateHubsWebTourMutation';
import updateHubsWebLinksMutation from './mutations/updateHubsWebLinksMutation';
import uploadPremiumHEMProfileImageMutation from './mutations/uploadPremiumHEMProfileImageMutation';
import updateHubsProfilesMutation from './mutations/updateHubsProfilesMutation';
import updateHubsSchoolProfilesMutation from './mutations/updateHubsSchoolProfilesMutation';
import basicInfoReducer from './BasicInfo/reducer';

import './styles.css';
import withReducer from '../../utils/withReducer';
import { REDUCER_NAME_BASIC_INFO } from '../../reducers';

import CollegeEditorService from './hemServices/collegeService';
import { initializeBasicInfo, setBasicInfoIsLoaded } from './BasicInfo/actions';
import { updateCMSRawData, setCMSDataIsInvalid, resetNonPersistentCollegeProfile } from './actions';

class CollegeProfileReact extends React.Component {
  static propTypes = {
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        jwtToken: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    viewer: PropTypes.object.isRequired,
    route: PropTypes.object.isRequired,
    uploadLogoMutation: PropTypes.func.isRequired,
    uploadPhotoMutation: PropTypes.func.isRequired,
    resetNonPersistentCollegeProfile: PropTypes.func.isRequired,
    updateHubsCollegeLogoMutation: PropTypes.func,
    updateHubsWebTourMutation: PropTypes.func,
    updateHubsWebLinksMutation: PropTypes.func,
    updateHubsProfilesMutation: PropTypes.func,
    updateHubsSchoolProfilesMutation: PropTypes.func,
    uploadPremiumHEMProfileImageMutation: PropTypes.func,
    initializeBasicInfo: PropTypes.func,
    setBasicInfoIsLoaded: PropTypes.func,
    updateCMSRawData: PropTypes.func,
    setCMSDataIsInvalid: PropTypes.func,
  };

  componentDidMount = () => {
    const college = get(this.props.viewer, 'collegeCore');
    const scid = get(college, 'codes.SCID');
    const baseHemURL = `${process.env.HUBS_EDIT_PROFILE_BACKEND}/colleges/scid/${scid}`;
    window.collegeService = new CollegeEditorService(
      college,
      this.props.current.viewer.jwtToken,
      null,
      this.props.initializeBasicInfo,
      this.props.setBasicInfoIsLoaded,
      this.props.updateCMSRawData,
      this.props.setCMSDataIsInvalid,
      baseHemURL,
    );
    // kick off (asynchronous) loading
    window.collegeService.load();
  };

  componentWillUnmount = () => {
    this.props.resetNonPersistentCollegeProfile();
  };

  render() {
    const {
      viewer,
      uploadLogoMutation: uploadLogo,
      uploadPhotoMutation: uploadPhoto,
      updateHubsCollegeLogoMutation: updateHubsCollegeLogo,
      updateHubsWebTourMutation: updateHubsWebTour,
      updateHubsWebLinksMutation: updateHubsWebLinks,
      updateHubsProfilesMutation: updateHubsProfiles,
      updateHubsSchoolProfilesMutation: updateHubsSchoolProfiles,
      uploadPremiumHEMProfileImageMutation: uploadPremiumHEMProfileImage,
      route,
    } = this.props;
    const enabledFeatures = get(viewer, 'enabledFeatures', []);
    const scid = get(viewer, 'collegeCore.codes.SCID');
    const hiid = get(viewer, 'collegeCore.codes.HIID');
    const collegeName = get(viewer, 'collegeCore.name');
    const hubsWebTour = get(viewer, 'hubsWebTour');
    const hubsWebLinks = get(viewer, 'hubsWebLinks');
    const hubsCustomProfilesResponse = get(viewer, 'hubsProfiles.custom_profiles');
    const hubsSchoolProfileResponse = get(viewer, 'hubsSchoolProfiles');
    let hubsCustomProfiles = [];
    hubsCustomProfiles.push(hubsSchoolProfileResponse);
    hubsCustomProfiles = hubsCustomProfiles.concat(hubsCustomProfilesResponse);

    return (
      <div styleName="edit-wrapper">
        <PageTitle title="College Profile" category="Naviance" />
        <PremiumMenu
          route={route}
          scid={scid}
          hiid={hiid}
          collegeName={collegeName}
          logoUpload={uploadLogo}
          photoUpload={uploadPhoto}
          updateHubsCollegeLogo={updateHubsCollegeLogo}
          updateHubsWebTour={updateHubsWebTour}
          updateHubsWebLinks={updateHubsWebLinks}
          updateHubsProfiles={updateHubsProfiles}
          updateHubsSchoolProfiles={updateHubsSchoolProfiles}
          profilePhotoUpload={uploadPremiumHEMProfileImage}
          hubsCustomProfiles={hubsCustomProfiles}
          hubsWebTour={hubsWebTour}
          hubsWebLinks={hubsWebLinks}
          enabledFeatures={enabledFeatures}
        />
      </div>
    );
  }
}

export const CollegeProfileReactEditComponent = injectIntl(CollegeProfileReact);

const query = gql`
  query CollegeProfileEditPage {
    viewer {
      id
      enabledFeatures
      collegeCore {
        id
        name
        codes {
          SCID
          HIID
        }
      }
      hubsSchoolProfiles {
        img
        img_thumb_large
        type
        logo
        body
        headline
        quote
        product_id
      }
      hubsProfiles {
        custom_profiles {
          img
          img_thumb_large
          type
          logo
          body
          headline
          quote
          best_place_to_eat
          class
          department
          desc
          fav_event
          fav_hangout
          fav_tradition
          hometown
          major
          persons_name
          program_name
          travel
          add_facts
          imgDisplayUrl
          logoDisplayUrl
          product_id
        }
      }
      hubsWebTour {
        campus_media {
          active
          date_from
          date_to
          product_id
          assets {
            title
            order
            caption
            media_type
            media_url
            mediaDisplayUrl
          }
        }
      }
      hubsCollegeLogo {
        college_logo {
          logoDisplayUrl
        }
      }
      hubsWebLinks {
        premium_links {
          active
          url
          info
          topic
          category
          order
          begin_date
          end_date
          product_id
        }
      }
    }
  }
`;

const uploadLogo = graphql(uploadLogoMutation, {
  name: 'uploadLogoMutation',
});

const uploadPhoto = graphql(uploadPhotoMutation, {
  name: 'uploadPhotoMutation',
});

const updateHubsCollegeLogo = graphql(updateHubsCollegeLogoMutation, {
  name: 'updateHubsCollegeLogoMutation',
});

const updateHubsWebTour = graphql(updateHubsWebTourMutation, {
  name: 'updateHubsWebTourMutation',
});

const updateHubsWebLinks = graphql(updateHubsWebLinksMutation, {
  name: 'updateHubsWebLinksMutation',
});

const updateHubsProfiles = graphql(updateHubsProfilesMutation, {
  name: 'updateHubsProfilesMutation',
});

const updateHubsSchoolProfiles = graphql(updateHubsSchoolProfilesMutation, {
  name: 'updateHubsSchoolProfilesMutation',
});

const uploadPremiumHEMProfileImage = graphql(
  uploadPremiumHEMProfileImageMutation,
  {
    name: 'uploadPremiumHEMProfileImageMutation',
  },
);

export default compose(
  connect(
    null,
    {
      resetNonPersistentCollegeProfile,
      initializeBasicInfo,
      setBasicInfoIsLoaded,
      updateCMSRawData,
      setCMSDataIsInvalid,
    },
  ),
  SimpleLoadingComponent(
    graphql(query),
    uploadLogo,
    uploadPhoto,
    updateHubsCollegeLogo,
    updateHubsWebTour,
    updateHubsWebLinks,
    updateHubsProfiles,
    updateHubsSchoolProfiles,
    uploadPremiumHEMProfileImage,
  ),
  withReducer(REDUCER_NAME_BASIC_INFO, basicInfoReducer),
)(CollegeProfileReactEditComponent);
